import { doc, getDoc, setDoc, updateDoc, onSnapshot, arrayUnion, arrayRemove, runTransaction } from 'firebase/firestore';
import { db } from './config';

// Next sequential invoice number for this business (atomic across devices).
// Returns e.g. "INV-0001". A gap can occur if generation later fails — that's
// fine; invoice numbers must be unique, not perfectly contiguous.
//
// When the user belongs to a company, the counter lives on the COMPANY doc so
// all colleagues draw from ONE shared sequence — otherwise two members billing
// under the same business name could both mint "INV-0001". Solo users keep a
// per-user counter.
export const getNextInvoiceNumber = async (uid, companyId = null) => {
  const ref = companyId ? doc(db, 'companies', companyId) : doc(db, 'userSettings', uid);
  const n = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const cur = (snap.exists() && snap.data().invoiceCounter) || 0;
    const next = cur + 1;
    tx.set(ref, { invoiceCounter: next }, { merge: true });
    return next;
  });
  return 'INV-' + String(n).padStart(4, '0');
};

// Live per-user settings (currently: custom task/milestone categories).
export const subscribeToUserSettings = (uid, callback) => {
  return onSnapshot(doc(db, 'userSettings', uid), (snap) => {
    callback(snap.exists() ? snap.data() : { customCategories: [] });
  });
};

export const addCustomCategory = async (uid, category) => {
  const ref = doc(db, 'userSettings', uid);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await updateDoc(ref, { customCategories: arrayUnion(category) });
  } else {
    await setDoc(ref, { customCategories: [category] });
  }
};

export const removeCustomCategory = (uid, category) =>
  updateDoc(doc(db, 'userSettings', uid), { customCategories: arrayRemove(category) });

export const addCustomNoteTag = async (uid, tag) => {
  const ref = doc(db, 'userSettings', uid);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await updateDoc(ref, { customNoteTags: arrayUnion(tag) });
  } else {
    await setDoc(ref, { customNoteTags: [tag] });
  }
};

export const removeCustomNoteTag = (uid, tag) =>
  updateDoc(doc(db, 'userSettings', uid), { customNoteTags: arrayRemove(tag) });

// Custom incident "Type" tags for the Health & Lameness section — reusable
// across incidents once added.
export const addCustomIncidentTag = async (uid, tag) => {
  const ref = doc(db, 'userSettings', uid);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await updateDoc(ref, { customIncidentTags: arrayUnion(tag) });
  } else {
    await setDoc(ref, { customIncidentTags: [tag] });
  }
};

export const removeCustomIncidentTag = (uid, tag) =>
  updateDoc(doc(db, 'userSettings', uid), { customIncidentTags: arrayRemove(tag) });

// Per-user, per-horse collapsed/expanded layout of the profile sections.
// Stored as sectionLayout: { [horseId]: { pedigree: true, ... } }. Merge keeps
// other horses' layouts intact.
export const setHorseSectionLayout = (uid, horseId, layout) =>
  setDoc(doc(db, 'userSettings', uid), { sectionLayout: { [horseId]: layout } }, { merge: true });

// Built-in categories/tags the user has chosen to hide, so they're excluded from
// pickers and shown in a "Hidden Built-in" list with the option to restore.
export const hidePresetCategory = async (uid, category) => {
  const ref = doc(db, 'userSettings', uid);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await updateDoc(ref, { hiddenCategories: arrayUnion(category) });
  } else {
    await setDoc(ref, { hiddenCategories: [category] });
  }
};

export const unhidePresetCategory = (uid, category) =>
  updateDoc(doc(db, 'userSettings', uid), { hiddenCategories: arrayRemove(category) });

export const hidePresetNoteTag = async (uid, tag) => {
  const ref = doc(db, 'userSettings', uid);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await updateDoc(ref, { hiddenNoteTags: arrayUnion(tag) });
  } else {
    await setDoc(ref, { hiddenNoteTags: [tag] });
  }
};

export const unhidePresetNoteTag = (uid, tag) =>
  updateDoc(doc(db, 'userSettings', uid), { hiddenNoteTags: arrayRemove(tag) });

// Custom document types for the Pictures & Documents section (e.g. "Breeding
// Contract") — reusable across horses once added, like categories/tags.
export const addCustomDocType = async (uid, docType) => {
  const ref = doc(db, 'userSettings', uid);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await updateDoc(ref, { customDocTypes: arrayUnion(docType) });
  } else {
    await setDoc(ref, { customDocTypes: [docType] });
  }
};

export const removeCustomDocType = (uid, docType) =>
  updateDoc(doc(db, 'userSettings', uid), { customDocTypes: arrayRemove(docType) });

export const addCustomLocation = async (uid, location) => {
  const ref = doc(db, 'userSettings', uid);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await updateDoc(ref, { customLocations: arrayUnion(location) });
  } else {
    await setDoc(ref, { customLocations: [location] });
  }
};

export const removeCustomLocation = (uid, location) =>
  updateDoc(doc(db, 'userSettings', uid), { customLocations: arrayRemove(location) });

// Business profile for invoices (name/contact/terms). Merged so it doesn't
// disturb the user's other settings. Applies to trainers, boarding facilities,
// or anyone who bills a horse's owner.
export const saveBusinessProfile = (uid, profile) =>
  setDoc(doc(db, 'userSettings', uid), { businessProfile: profile }, { merge: true });

// A provider's own service chips. Normally the day sheet learns these from
// their recent charges; anything they edit by hand is pinned here and shown
// first, in their order, instead of being re-ranked.
export const saveServiceChips = (uid, chips, hidden = []) =>
  setDoc(doc(db, 'userSettings', uid), { serviceChips: chips, hiddenServiceChips: hidden }, { merge: true });
